import { createReducer } from '@reduxjs/toolkit';
import { IOrderDto } from '../../api/ClientApi';
import { myAssert } from '../../common/console';

import { acceptOrder, clearOrderForOrderAcception, importOrderForAcception } from '../actions/orderActions';
import {
    clearBaseEntityState,
    compareModifiedDate,
    IBaseEntityState,
    IModifiedInfo,
    SaveEntityStatus,
    setBaseEntityCurrentJson,
} from './common';

export interface AcceptOrderFormReducerState extends IBaseEntityState {
    order: IOrderDto | null;
}

function clearState(state: AcceptOrderFormReducerState): void {
    state.order = null;

    clearBaseEntityState(state);
}

function getInitialState(): AcceptOrderFormReducerState {
    const state: AcceptOrderFormReducerState = {
        order: null,
        baseJsonString: null,
        currentJsonString: null,
        isChanged: undefined,
        saveStatus: SaveEntityStatus.None,
        modified: undefined,
    };

    clearState(state);

    return state;
}

function calculateCurrentJson(state: AcceptOrderFormReducerState, setAsBaseline: boolean = false): void {
    myAssert(() => state.order != null, 'Need order to calculate');

    const jsonBaseline = JSON.stringify(state.order);

    setBaseEntityCurrentJson(state, jsonBaseline, setAsBaseline);
}

function calculateBaseInfo(state: AcceptOrderFormReducerState) {
    myAssert(() => state.order != null, 'Need order to calculate');

    if (!state.order?.id || state.order?.id <= 0) {
        // no modified data should be set
    } else {
        // lets find the last modified part of order
        const modified: IModifiedInfo | undefined = compareModifiedDate(state.order, undefined);

        state.modified =
            (modified ?? state.order?.created)
                ? { modifiedAt: state.order?.created ?? new Date(), modifiedBy: state.order?.createdBy ?? '' }
                : undefined;
    }
}

export const acceptOrderFormReducer = createReducer<AcceptOrderFormReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(importOrderForAcception.fulfilled, (state: AcceptOrderFormReducerState, action: any) => {
                clearState(state);

                const payload: IOrderDto = action.payload;

                myAssert(() => !!payload && !!payload.orderStatus, 'Required Data for Import');

                state.order = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(clearOrderForOrderAcception, (state: AcceptOrderFormReducerState, action: any) => {
                clearState(state);
            })
            .addCase(acceptOrder.pending, (state: AcceptOrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(acceptOrder.fulfilled, (state: AcceptOrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(acceptOrder.rejected, (state: AcceptOrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            });
    },
);
